import { z } from 'zod';
import { countryCodeSchema, currencyCodeSchema, optionalText, patchOf } from './common.js';
import { roleSchema } from './permissions.js';
import { emailSchema, optionalEmailSchema } from './auth.js';
export const TAX_ID_TYPES = ['tin', 'rc_number', 'bn_number', 'other'];
export const taxIdentifierTypeSchema = z.enum(TAX_ID_TYPES);
export const WORKFLOW_MODES = ['standalone', 'nrs_optional', 'nrs_required'];
export const workflowModeSchema = z.enum(WORKFLOW_MODES);
export const addressFieldsShape = {
    streetName: optionalText(200),
    cityName: optionalText(100),
    stateCode: optionalText(10),
    lgaCode: optionalText(20),
    postalZone: optionalText(20),
    countryCode: countryCodeSchema.default('NG'),
};
export const addressSchema = z.object(addressFieldsShape);
const phone = optionalText(30);
export const businessInputSchema = z.object({
    legalName: z.string().trim().min(2, 'Enter the registered name').max(200),
    displayName: z.string().trim().min(1, 'Enter a display name').max(120),
    businessType: optionalText(80),
    email: emailSchema,
    phone,
});
export const businessUpdateSchema = patchOf(businessInputSchema);
export const taxpayerInputSchema = z.object({
    legalName: z.string().trim().min(2, 'Enter the legal name').max(200),
    taxIdentifier: optionalText(40),
    taxIdentifierType: taxIdentifierTypeSchema.nullish(),
    email: optionalEmailSchema,
    phone,
    ...addressFieldsShape,
    invoiceNumberPrefix: z
        .string()
        .trim()
        .max(12)
        .regex(/^[A-Za-z0-9-]*$/, 'Letters, numbers and hyphens only')
        .nullish()
        .transform((v) => (v === undefined ? undefined : v ? v.toUpperCase() : null)),
    defaultCurrencyCode: currencyCodeSchema.default('NGN'),
    paymentTermsDays: z.number().int().min(0).max(365).default(30),
    invoiceFooterNote: optionalText(1000),
});
export const taxpayerUpdateSchema = patchOf(taxpayerInputSchema).extend({
    invoiceWorkflowMode: workflowModeSchema.optional(),
    status: z.enum(['active', 'inactive']).optional(),
});
/** First-time onboarding: one request creates the business and its first taxpayer profile. */
export const onboardingSchema = z.object({
    business: businessInputSchema,
    taxpayer: taxpayerInputSchema,
    createMainBranch: z.boolean().default(true),
});
export const branchInputSchema = z.object({
    branchCode: z
        .string()
        .trim()
        .min(1, 'Enter a branch code')
        .max(20)
        .regex(/^[A-Za-z0-9_-]+$/, 'Letters, numbers, hyphens and underscores only'),
    name: z.string().trim().min(1, 'Enter a branch name').max(120),
    ...addressFieldsShape,
    status: z.enum(['active', 'inactive']).default('active'),
});
export const branchUpdateSchema = patchOf(branchInputSchema);
export const inviteMemberSchema = z.object({
    email: emailSchema,
    fullName: z.string().trim().min(2).max(120).optional(),
    role: roleSchema.exclude(['owner']),
});
export const updateMemberSchema = z.object({
    role: roleSchema.exclude(['owner']).optional(),
    status: z.enum(['active', 'revoked']).optional(),
});
// ───────────── responses ─────────────
export const businessSchema = z.object({
    id: z.uuid(),
    legalName: z.string(),
    displayName: z.string(),
    businessType: z.string().nullable(),
    email: z.string(),
    phone: z.string().nullable(),
    status: z.enum(['active', 'suspended', 'archived']),
    createdAt: z.string(),
});
export const taxpayerSchema = z.object({
    id: z.uuid(),
    businessId: z.uuid(),
    legalName: z.string(),
    taxIdentifier: z.string().nullable(),
    taxIdentifierType: taxIdentifierTypeSchema.nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    streetName: z.string().nullable(),
    cityName: z.string().nullable(),
    stateCode: z.string().nullable(),
    lgaCode: z.string().nullable(),
    postalZone: z.string().nullable(),
    countryCode: z.string(),
    invoiceNumberPrefix: z.string().nullable(),
    invoiceWorkflowMode: workflowModeSchema,
    defaultCurrencyCode: z.string(),
    paymentTermsDays: z.number(),
    invoiceFooterNote: z.string().nullable(),
    status: z.enum(['active', 'inactive', 'archived']),
    createdAt: z.string(),
});
export const branchSchema = z.object({
    id: z.uuid(),
    taxpayerProfileId: z.uuid(),
    branchCode: z.string(),
    name: z.string(),
    streetName: z.string().nullable(),
    cityName: z.string().nullable(),
    stateCode: z.string().nullable(),
    lgaCode: z.string().nullable(),
    postalZone: z.string().nullable(),
    countryCode: z.string(),
    status: z.enum(['active', 'inactive']),
});
export const memberSchema = z.object({
    id: z.uuid(),
    userId: z.uuid(),
    email: z.string(),
    fullName: z.string(),
    role: roleSchema,
    status: z.enum(['invited', 'active', 'revoked']),
    mfaEnabled: z.boolean(),
    lastLoginAt: z.string().nullable(),
    joinedAt: z.string().nullable(),
    invitedAt: z.string(),
});
export const businessContextSchema = z.object({
    business: businessSchema,
    role: roleSchema,
    permissions: z.array(z.string()),
    taxpayers: z.array(taxpayerSchema),
});
export const auditLogEntrySchema = z.object({
    id: z.uuid(),
    actorType: z.enum(['user', 'system', 'integration']),
    actorId: z.string().nullable(),
    actorName: z.string().nullable(),
    action: z.string(),
    resourceType: z.string(),
    resourceId: z.string().nullable(),
    metadata: z.unknown().nullable(),
    createdAt: z.string(),
});
//# sourceMappingURL=business.js.map